import { useEffect, useState } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { usePageContent, useSiteData } from '@/context/SiteDataContext';
import { useEditMode } from '@/context/EditModeContext';
import PageHero from '@/components/PageHero';

const DEFAULTS = {
  privacy: {
    heading: 'Privacy Policy',
    sections: [
      { heading: 'Introduction', body: '[Placeholder] Describe how Wisconsin First collects, uses, and protects personal information.' },
      { heading: 'Information We Collect', body: '[Placeholder] List the information collected through forms, prayer requests, newsletter sign-ups, and accounts.' },
      { heading: 'Contact Us', body: '[Placeholder] Explain how visitors can reach you with privacy questions.' },
    ],
  },
  terms: {
    heading: 'Terms of Use',
    sections: [
      { heading: 'Acceptance of Terms', body: '[Placeholder] Describe the terms visitors agree to by using this website.' },
      { heading: 'Use of Content', body: '[Placeholder] Explain how website content, photos, and media may be used.' },
      { heading: 'Contact Us', body: '[Placeholder] Explain how visitors can reach you with questions about these terms.' },
    ],
  },
};

export default function LegalPage({ slug }) {
  const c = usePageContent(slug);
  const { editMode } = useEditMode();
  const d = DEFAULTS[slug];
  const sections = c.sections || d.sections;
  return (
    <div>
      <PageHero slug={slug} overline="Wisconsin First" heading={d.heading} text="" />
      <section className="bg-white py-16">
        <div className="max-w-3xl mx-auto px-6" data-testid={`legal-${slug}`}>
          {editMode ? <SectionsEditor slug={slug} initial={sections} /> : sections.map((s, i) => (
            <div key={i} className="mb-10">
              <h2 className="font-serif text-2xl text-royal font-bold mb-3">{s.heading}</h2>
              <p className="text-charcoal-muted leading-relaxed whitespace-pre-line">{s.body}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function SectionsEditor({ slug, initial }) {
  const { loadPageContent } = useSiteData();
  const [list, setList] = useState(initial);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setList(initial); }, [initial]);
  const upd = (i, k, v) => setList(list.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  const move = (i, d) => { const n = [...list]; [n[i], n[i + d]] = [n[i + d], n[i]]; setList(n); };
  const save = async () => {
    setBusy(true);
    try { await api.patch(`/site/page-content/${slug}`, { sections: list }); await loadPageContent(slug); toast.success('Saved'); }
    catch { toast.error('Save failed'); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      {list.map((s, i) => (
        <div key={i} className="border rounded-lg p-4 space-y-2" data-testid={`legal-section-${i}`}>
          <div className="flex gap-2">
            <input value={s.heading} onChange={(e) => upd(i, 'heading', e.target.value)} data-testid={`legal-heading-${i}`} className="flex-1 px-3 py-2 border rounded font-semibold" />
            <button disabled={i === 0} onClick={() => move(i, -1)} className="p-2 disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
            <button disabled={i === list.length - 1} onClick={() => move(i, 1)} className="p-2 disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
            <button onClick={() => setList(list.filter((_, j) => j !== i))} className="p-2 text-red-500"><Trash2 className="w-4 h-4" /></button>
          </div>
          <textarea rows={5} value={s.body} onChange={(e) => upd(i, 'body', e.target.value)} data-testid={`legal-body-${i}`} className="w-full px-3 py-2 border rounded" />
        </div>
      ))}
      <div className="flex justify-between">
        <button onClick={() => setList([...list, { heading: 'New section', body: '' }])} data-testid="legal-add-section" className="inline-flex items-center gap-1 text-sm px-4 py-2 rounded-full bg-royal/10 text-royal"><Plus className="w-4 h-4" /> Add section</button>
        <button onClick={save} disabled={busy} data-testid="legal-save" className="btn-royal text-sm">{busy ? 'Saving…' : 'Save page'}</button>
      </div>
    </div>
  );
}
